import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReusableTable from "components/Tables/ReusableTable";
import { Flex, Box, Text, Badge } from "@chakra-ui/react";
import { Plus, EllipsisVertical } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { formatDate } from "utils/dateFormatter";
import GradientButton from "components/Buttons/GradientButton";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";

const CommissionManager = ({
    commissionType = "Driver", // Can be "Driver" or "Paygate"
    fetchCommissionsAction,
    addCommissionAction,
    selectorKey,
    AddCommissionForm,
    ViewCommissions,
    icon = null,
}) => {
    const dispatch = useDispatch();
    const { commissions, loading } = useSelector((state) => state.commissions || {});
    const [isAddCommissionDrawerOpen, setAddCommissionDrawerOpen] = useState(false);
    const [isViewCommissionsDrawerOpen, setViewCommissionsDrawerOpen] = useState(false);
    const [selectedCommission, setSelectedCommission] = useState(null);

    useEffect(() => {
        dispatch(fetchCommissionsAction());
    }, [dispatch]);


    // Filter commissions by commissionType (Driver or Paygate)
    const filteredCommissions = useMemo(() => {
        if (!Array.isArray(commissions)) return [];
        return commissions.filter((commission) => commission.commission_type === commissionType);
    }, [commissions, commissionType]);

const tableData = useMemo(() => {
    if (filteredCommissions.length === 0) return [];

    // Find the latest commission that has met its trigger time
    const activeCommission = filteredCommissions
        .filter(commission => new Date(commission.trigger_date) <= new Date())
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

    return filteredCommissions.map((commission) => {
        const isActive = activeCommission?.commission_id === commission.commission_id;

        return {
            commissionPercentage: commission.commission_percentage,
            createdAt: commission.created_at ? formatDate(commission.created_at) : "N/A",
            triggerAt: commission.trigger_date ? formatDate(commission.trigger_date) : "N/A",
            status: isActive ? "Active" : "Inactive",
            original: commission,
        };
    });
}, [filteredCommissions]);

    const columnsData = [
        {
            Header: "Commission Percentage (%)",
            accessor: "commissionPercentage",
            width: '20%',
        },
        {
            Header: "Created",
            accessor: "createdAt",
            width: '20%',
        },
        {
            Header: "Trigger At",
            accessor: "triggerAt",
            width: '20%',
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <Badge
                    colorScheme={value === "Active" ? "green" : "yellow"}
                    borderRadius="full"
                    px={2}
                >
                    {value}
                </Badge>
            ),
            width: '15%',
        },
        {
            Header: "Actions",
            accessor: "moreOptions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={20} color="grey" />}
                    items={[
                        {
                            icon,
                            text: `View ${commissionType} Commission Details`,
                            onClick: () => handleViewCommissions(row.original),
                        },
                    ]}
                />
            ),
            width: '5%',
        },
    ];

    const handleAddCommission = async (formData) => {
        try {
            await dispatch(addCommissionAction(formData));
            dispatch(fetchCommissionsAction());
        } catch (error) {
            console.error("Error adding commission:", error);
        }
    };

    const handleViewCommissions = (commission) => {
        setSelectedCommission(commission);
        setViewCommissionsDrawerOpen(true);
    };

    return (
        <Flex flexDirection="column">
            <Box p={6}>
                <Flex justifyContent="space-between" alignItems="center" mb={4}>
                    {loading ? (
                        <Skeleton height="20px" width="160px" />
                    ) : (
                        <Text fontSize="md" fontWeight="bold">
                            {commissionType} Commissions: {filteredCommissions?.length ?? 0}
                        </Text>
                    )}

                    {loading ? (
                        <Skeleton height="40px" width="250px" borderRadius="md" />
                    ) : (
                        <GradientButton
                            icon={<Plus size={16} />}
                            onClick={() => setAddCommissionDrawerOpen(true)}
                            height={"40px"}
                        >
                            Add {commissionType} Commission Percentage
                        </GradientButton>
                    )}
                </Flex>
            </Box>
            <ReusableTable
                columnsData={columnsData}
                tableData={tableData}
                initialState={{
                    pageSize: 10,
                    sortBy: [{ id: 'createdAt', desc: true }],
                }}
                isLoading={loading}
            />

            <AddCommissionForm
                isOpen={isAddCommissionDrawerOpen}
                onClose={() => setAddCommissionDrawerOpen(false)}
                onAddCommission={handleAddCommission}
                commissionType={commissionType}
            />

            <ViewCommissions
                isOpen={isViewCommissionsDrawerOpen}
                onClose={() => setViewCommissionsDrawerOpen(false)}
                commission={selectedCommission}
                commissionType={commissionType}
            />
        </Flex>
    );
};

export default CommissionManager;
